import { Injectable, Optional } from "@angular/core";
import { Observable } from "rxjs";
import { map } from 'rxjs/operators';
import { IMetadata } from "../../../../dev/components/elad.forms2/interfaces/metadata";
import {
	IMetadataFlatTable,
	IMetadataFlatTableField
} from "../../../../dev/components/elad.forms2/interfaces/metadata/imetadata.flat.table";
import { IMetadataGroup } from "../../../../dev/components/elad.forms2/interfaces/metadata/imetadata.group";
import { LookupsService, OperatorFilter } from "../../../lookups";
import { LogicFilter } from "../../../lookups/interfaces/Ifilter.lookup";
import { MetadataProxy } from "./metadata.proxy";
import { MetadataUtils } from "app.services/dynamic.forms/services/metadata.utils";

/**
 * Created by sagil on 07/12/2016.
 */

export const FULL_MODEL_PROP_NAME = 'Patient';

@Injectable({ providedIn: 'root' })
  export class MetadataService {
	public _patientMetadata : IMetadataGroup;
	public _patientFlatMetadata : IMetadataFlatTable[];

	constructor(
		private proxy: MetadataProxy,
		private lookupsService: LookupsService,
		@Optional() private metadataUtils: MetadataUtils) {
			
	}

	public getPatientMetadata(): Observable<IMetadataGroup> {
		return this.proxy.getPatientMetadata().pipe(map((x) => {
			this._patientMetadata = x.patient;
			this.addFullModelKey();
			this._patientFlatMetadata = this.getFlatMetadata(this._patientMetadata);
			return this._patientMetadata;
		}));
	}

	public insert(objectName:string, keyName:string, dataType:string) {
		return this.lookupsService.insert("ModelMetadata", {
			objectName, keyName, dataType
		}).pipe(map(res => {
			let metadata : IMetadata = { 'type' : dataType, 'virtual' : true, 'defaults' : {}};

			this._insert({'patient' : {type: 'group', fields: this._patientMetadata}}, objectName, keyName, metadata);

			this._patientMetadata = Object.assign({}, this._patientMetadata);
			this._patientFlatMetadata = this.getFlatMetadata(this._patientMetadata);

			return res;
		}));
	}

	protected _insert(group: IMetadataGroup, objectName:string, keyName:string, row:IMetadata) : boolean {
		for (let key in group) {
			if (!group.hasOwnProperty(key)) {
				continue;
			}

			if (group[key].type !== 'group') {
				continue;
			}

			if (key === objectName) {
				if (key !== 'patient') {
					group[key] = Object.assign({}, group[key]);
					group[key].fields = Object.assign({}, group[key].fields);
				}

				group[key].fields[keyName] = row;
				return true;
			}

			if (this._insert(group[key].fields, objectName, keyName, row)) {
				group[key] = Object.assign({}, group[key]);
				group[key].fields = Object.assign({}, group[key].fields);
			}
		}

		return false;
	}

	public delete(objectName:string, keyName:string) {
		return this.lookupsService.delete({
			lookupName: "ModelMetadata",
			filter: this.lookupsService.createComplexFilter(LogicFilter.and, [
				this.lookupsService.createBaseFilter("objectName", OperatorFilter.eq, objectName, false),
				this.lookupsService.createBaseFilter("keyName", OperatorFilter.eq, keyName, false)])
		}).pipe(map(res => {
			this._delete({'patient' : {type: 'group', fields: this._patientMetadata}}, objectName, keyName);

			this._patientMetadata = Object.assign({}, this._patientMetadata);
			this._patientFlatMetadata = this.getFlatMetadata(this._patientMetadata);

			return res;
		}));
	}

	protected _delete(group: IMetadataGroup, objectName:string, keyName:string) : boolean {
		for (let currObject in group) {
			if (!group.hasOwnProperty(currObject)) {
				continue;
			}

			if (group[currObject].type !== 'group') {
				continue;
			}

			if (currObject === objectName) {
				for (let currentKey in group[currObject].fields) {
					if (!group[currObject].fields.hasOwnProperty(currentKey)) {
						continue;
					}

					if (currentKey === keyName) {
						if (objectName !== 'patient') {
							group[currObject] = Object.assign({}, group[currObject]);
							group[currObject].fields = Object.assign({}, group[currObject].fields);
						}

						delete group[currObject].fields[keyName];
						return true;
					}
				}

				return false;
			} else if (this._delete(group[currObject].fields, objectName, keyName)) {
				group[currObject] = Object.assign({}, group[currObject]);
				group[currObject].fields = Object.assign({}, group[currObject].fields);
			}
		}

		return false;
	}

	protected addFullModelKey() {
		this._patientMetadata = Object.assign({
			[FULL_MODEL_PROP_NAME]:  {
				type: 'full-model'
			}
		}, this._patientMetadata);
	}

	public get patientAsMeta() : IMetadata {
		return {
			type: 'group',
			fields: this.patient
		};
	}

	public get patient() : IMetadataGroup {
		return this._patientMetadata;
	}

	// expecting path like visit[0].orders[0]
	public metadataForPath(path:string,patientOrLookup:IMetadataGroup):IMetadata{
		let subPaths = path.split(".");
		let currMetaData=patientOrLookup;
		for (let i=0;i<=subPaths.length-2;++i){
			let currNoIdx=subPaths[i].substr(0,subPaths[i].indexOf("["));
			currMetaData=currMetaData[currNoIdx]["fields"];
		}
		let lastpath=subPaths[subPaths.length-1];
		let lastPathNoIndex = lastpath.indexOf('[')>-1?lastpath.substr(0,lastpath.indexOf('[')):lastpath;
		return currMetaData[lastPathNoIndex];
	}
	public get address() : IMetadataGroup {
		return this.patient["address"]["fields"];
	}
	public get visit() : IMetadataGroup {
		return this.patient['visit']['fields'];
	}

	public get order() : IMetadataGroup {
		return this.visit['orders']['fields'];
	}

	public get orderedProcedureItem(): IMetadataGroup {
		return this.order['orderedProcedureItem']['fields'];
	}

	public get requestedProcedure() : IMetadataGroup {
		return this.order['requestedProcedure']['fields'];
	}

	public get sps() : IMetadataGroup {
		return this.requestedProcedure['sps']['fields'];
	}

	public get spsItem() : IMetadataGroup {
		return this.sps["spsItem"]["fields"];
	}

	public get spsXPps() : IMetadataGroup {
		return this.sps['spsXPps']['fields'];
	}

	public get pps() : IMetadataGroup {
		return this.spsXPps['pps']['fields'];
	}

	public get ppsItem(): IMetadataGroup {
		return this.pps['ppsItem']['fields'];
	}

	public get spsPersonReference(): IMetadataGroup {
		return this.sps['spsPersonReference']['fields'];
	}

	public get interpretation(): IMetadataGroup {
		return this.pps['interpretation']['fields'];
	}

	public get flatPatientMetadata() : IMetadataFlatTable[] {
		return this._patientFlatMetadata;
	}

	public getFlatMetadata(metadata:IMetadataGroup, parent?:{type:string, fullPath:string, tableName:string}) {
		let flatMetadata = [];

		let table:IMetadataFlatTable = <IMetadataFlatTable>{};

		table.tableName = parent ? parent.tableName : 'patient';
		table.tableMetadata = {type: parent ? parent.type : 'full-model'};
		table.fullPath = parent ? parent.fullPath : 'Patient';
		table.fields = [];

		Object.keys(metadata).forEach(keyName => {
			if (metadata[keyName].type !== 'full-model') {
				if (metadata[keyName].type !== 'group') {
					let field:IMetadataFlatTableField = {...metadata[keyName], fullPath:keyName};
					table.fields.push(field);
				} else {
					this.getFlatMetadataSubTables(flatMetadata ,'', keyName, metadata[keyName]);
				}
			} else {
				table.tableMetadata = metadata[keyName];
			}
		});

		flatMetadata.push(table);
		flatMetadata = flatMetadata.map((item, _, arr) => ({
			...item,
			['level']: arr.filter(x => x.tableName === item.tableName).length < 2 ? undefined : item['level']
		}));

		return flatMetadata;
	}
	
	private getFlatMetadataSubTables(flatMetadata, path:string, tableName:string, group:IMetadata) {
		let table:IMetadataFlatTable = <IMetadataFlatTable>{};
		table.tableName = tableName;
		table.tableMetadata = group;
		table.fullPath = path+tableName;
		table.fields = [];
		if (tableName === 'scanDocument' || tableName.startsWith('siteProt') || tableName === 'spsXPps') {
			table.level = this.metadataUtils?.getPatientModelLevelByFieldPath(path);
		}
		if (group.fields && (flatMetadata.findIndex(x => x.tableName === tableName) < 0 || table.level)) {
			Object.keys(group.fields).forEach(keyName => {
				if (group.fields[keyName].type !== 'group') {
					let field:IMetadataFlatTableField = {...group.fields[keyName], fullPath:path + tableName + '[0].' + keyName};
					table.fields.push(field);
				} else {
					this.getFlatMetadataSubTables(flatMetadata, path + tableName + '[0].', keyName, group.fields[keyName]);
				}
			});
		}

		flatMetadata.push(table);
	}
}
